import { StepProps, Specialization, License } from '@/types/broker-registration.types';
import { SpecializationBlock } from './SpecializationBlock';
import { LicenseBlock } from './LicenseBlock';
import { useLanguage } from '@/contexts/LanguageContext';

export function StepTwo({ formData, updateFormData, errors, clearError }: StepProps) {
  const { t } = useLanguage();
  const addSpecialization = () => {
    const newSpecializations: Specialization[] = [
      ...formData.specializations,
      { industry: '', expertises: [], experienceYears: '' },
    ];
    updateFormData({ specializations: newSpecializations });
  };

  const removeSpecialization = (index: number) => {
    if (formData.specializations.length > 1) {
      const newSpecializations = formData.specializations.filter((_, i) => i !== index);
      updateFormData({ specializations: newSpecializations });
    }
  };

  const updateSpecialization = (
    index: number,
    field: keyof Specialization,
    value: string | string[]
  ) => {
    const newSpecializations = [...formData.specializations];
    newSpecializations[index] = { ...newSpecializations[index], [field]: value };
    updateFormData({ specializations: newSpecializations });
  };

  const addLicense = () => {
    const newLicenses: License[] = [
      ...formData.licenses,
      {
        details: '',
        number: '',
        jurisdictions: [],
        otherJurisdictions: '',
      },
    ];
    updateFormData({ licenses: newLicenses });
  };

  const removeLicense = (index: number) => {
    if (formData.licenses.length > 1) {
      const newLicenses = formData.licenses.filter((_, i) => i !== index);
      updateFormData({ licenses: newLicenses });
    }
  };

  const updateLicense = (index: number, field: keyof License, value: string | string[]) => {
    const newLicenses = [...formData.licenses];
    newLicenses[index] = { ...newLicenses[index], [field]: value };
    updateFormData({ licenses: newLicenses });
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold border-b border-slate-200 pb-3 text-[#1D314D]">
        {t('broker_registration.step_two.title')}
      </h2>
      <p className="text-sm text-slate-600">
        {t('broker_registration.step_two.subtitle')}
      </p>

      <h3 className="text-lg font-semibold mt-6 mb-4 text-[#1D314D]">
        {t('broker_registration.step_two.specializations_title')}
      </h3>

      <div id="specializations-container">
        {formData.specializations.map((spec, index) => (
          <SpecializationBlock
            key={index}
            specialization={spec}
            index={index}
            onUpdate={updateSpecialization}
            onRemove={removeSpecialization}
            canRemove={formData.specializations.length > 1}
            errors={errors}
            clearError={clearError}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={addSpecialization}
        className="mt-4 mb-8 flex items-center px-4 py-2 bg-[#1D314D] text-white font-semibold rounded-lg hover:bg-[#2E4663] transition duration-150"
      >
        <svg
          className="w-5 h-5 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
          ></path>
        </svg>
        {t('broker_registration.step_two.add_specialization')}
      </button>

      <h3 className="text-lg font-semibold mt-8 mb-4 text-[#1D314D]">
        {t('broker_registration.step_two.licenses_title')}
      </h3>

      <div id="licenses-container">
        {formData.licenses.map((license, index) => (
          <LicenseBlock
            key={index}
            license={license}
            index={index}
            onUpdate={updateLicense}
            onRemove={removeLicense}
            canRemove={formData.licenses.length > 1}
            errors={errors}
            clearError={clearError}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={addLicense}
        className="mt-4 flex items-center px-4 py-2 bg-[#1D314D] text-white font-semibold rounded-lg hover:bg-[#2E4663] transition duration-150"
      >
        <svg
          className="w-5 h-5 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
          ></path>
        </svg>
        {t('broker_registration.step_two.add_license')}
      </button>
    </div>
  );
}
